import { Dialog, DialogTitle, DialogContent, Box, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FancyProgressArc from '../../comps/FancyProgressArc'
import TraceArc from '../../comps/TraceArc'
import { FS_SM, ICON_SM } from '../../common/theme'
import type { TextStructure } from '../../types/textstructure'

interface LowTraceSentencesModalProps {
  open: boolean
  onClose: () => void
  // Current structure whose sentences we scan for low-sim entries.
  // Always renders in the RIGHT column when in compare mode.
  structure: TextStructure | null | undefined
  overallSimint?: number | null
  // Optional previous-version structure. When present the modal
  // renders the two side-by-side with `prevStructure` on the LEFT
  // (older) and the primary `structure` on the RIGHT (current).
  // Replaces the earlier `compareStructure` / `compareSimint`.
  prevStructure?: TextStructure | null
  prevSimint?: number | null
  labelLeft?: string
  labelRight?: string
  title?: string
  // Anything strictly below this sim percentage is flagged as "low".
  // Defaults to 40 to match the BidQuestionScoringTab's threshold.
  threshold?: number
}

function collectLowSentences(struct: TextStructure | null | undefined, threshold: number): any[] {
  return (struct?.paragraphs ?? [])
    .flatMap((p: any) => p.lines ?? [])
    .flatMap((l: any) => l.sentences ?? [])
    .filter((s: any) => typeof s.sim?.simint === 'number' && s.sim.simint < threshold && s.sentence && !s.deleted)
}

function SentenceList({ sentences, threshold }: { sentences: any[]; threshold: number }) {
  if (sentences.length === 0) {
    return (
      <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.6 }}>
        No sentences below {threshold}% similarity.
      </Typography>
    )
  }
  return (
    <>
      {sentences.map((s: any, i: number) => (
        <Box
          key={s.id ?? i}
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 0.75,
            mb: 0.75, p: 0.75, border: 1, borderColor: 'divider', borderRadius: 1,
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <FancyProgressArc value={s.sim.simint} zerona scale={0.7} />
          </Box>
          <Typography
            variant="caption"
            sx={{ fontSize: FS_SM, lineHeight: 1.5, opacity: 0.85 }}
            dangerouslySetInnerHTML={{ __html: s.sentence }}
          />
        </Box>
      ))}
    </>
  )
}

// Read-only popout listing every sentence with a trace-sim below
// `threshold`. Pass a single `structure` for a one-column view, or
// `structure` + `prevStructure` to render two columns side-by-side:
// `prevStructure` on the LEFT (older), `structure` on the RIGHT
// (current). Replaces the earlier compareStructure layout.
export default function LowTraceSentencesModal({
  open, onClose, structure, overallSimint, prevStructure, prevSimint,
  labelLeft, labelRight, title, threshold = 40,
}: LowTraceSentencesModalProps) {
  const isCompare = !!prevStructure
  const currLow = collectLowSentences(structure, threshold)
  const prevLow = collectLowSentences(prevStructure, threshold)

  const renderColumn = (label: string | undefined, simint: number | null | undefined, lows: any[]) => (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        {label && (
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM, opacity: 0.75 }}>
            {label}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {typeof simint === 'number' && (
          <TraceArc value={simint} tooltip={`Overall trace ${simint}%`} />
        )}
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM, opacity: 0.7, display: 'block', mb: 0.75 }}>
        {lows.length > 0
          ? `Low trace sentences (${lows.length}) - under ${threshold}% similarity to any source.`
          : 'No low-trace sentences. Every traced sentence sits comfortably above the threshold.'}
      </Typography>
      <SentenceList sentences={lows} threshold={threshold} />
    </Box>
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isCompare ? 'lg' : 'md'}
      fullWidth
      PaperProps={{ 'data-testid': 'low_trace_dlg' } as any}
    >
      <DialogTitle sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" variant="subtitle2" sx={{ fontWeight: 600, fontSize: FS_SM, flex: 1 }}>
          {title ?? 'Trace Similarity'}
        </Typography>
        <IconButton size="small" onClick={onClose} data-testid="low_trace_close_btn">
          <CloseIcon sx={{ fontSize: ICON_SM }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.25 }}>
        {isCompare ? (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            {/* Prev (older) on the LEFT, current on the RIGHT. */}
            {renderColumn(labelLeft ?? 'Previous', prevSimint, prevLow)}
            <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider', flexShrink: 0 }} />
            {renderColumn(labelRight ?? 'Current', overallSimint, currLow)}
          </Box>
        ) : (
          renderColumn(undefined, overallSimint, currLow)
        )}
      </DialogContent>
    </Dialog>
  )
}
